'use client';

import {sanitize} from 'isomorphic-dompurify';
import type React from 'react';
import {useCallback, useEffect, useRef} from 'react';

const HIDE_LAUNCHER_STYLE_REMOVAL_DELAY_MS = 500;

export interface GenesysChatProps {
  /** The Genesys Deployment ID for GFiber. */
  deploymentId?: string;
  /** The current environment (e.g., 'local', 'work', 'dev', 'tst', 'stg', 'prd'). */
  environment: 'local' | 'work' | 'dev' | 'tst' | 'stg' | 'prd';
  /** The source of the chat. */
  chatSource: 'Buyflow' | 'Portal' | 'Microapps' | 'Help Center';
  /** The name of the page to be sent to Genesys. */
  pageName?: string;
  /** The URL of the page to be sent to Genesys. */
  webPageAddress?: string;
  /** Whether to open the messenger immediately on load. */
  openOnLoad?: boolean;
  /** Callback when the messenger is closed. */
  onClosed?: () => void;
  /** Whether to show the default Genesys launcher/button. Defaults to false. */
  showLauncher?: boolean;
}

export type GenesysFunction = {
  (action: 'command', command: string, data?: unknown): void;
  (action: 'subscribe', event: string, callback: () => void): void;
  (action: 'unsubscribe', event: string, callback: () => void): void;
  (
    action: 'registerPlugin',
    pluginName: string,
    callback: (plugin: unknown) => void,
  ): void;
  q?: unknown[];
  t?: number;
  c?: unknown;
};

declare global {
  interface Window {
    Genesys?: GenesysFunction;
    _genesysJs?: string;
  }
}

interface GenesysWindow extends Window {
  Genesys?: {
    (...args: unknown[]): unknown;
    q?: unknown[][];
  };
}

const ensureGenesysInit = (deploymentId: string) => {
  if (typeof window === 'undefined') return;
  // Genesys Messenger Initialization
  // biome-ignore lint/suspicious/noExplicitAny: Genesys init script
  ((g: any, e: string, n: string, es: any, ys?: any) => {
    if (g[e]) return;
    g['_genesysJs'] = e;
    g[e] =
      g[e] ||
      ((...args: any[]) => {
        (g[e].q = g[e].q || []).push(args);
      });

    g[e].t = Date.now();
    g[e].c = es;

    ys = document.createElement('script');
    ys.async = 1;
    ys.src = n;
    ys.charset = 'utf-8';
    document.head.appendChild(ys);
  })(
    window,
    'Genesys',
    'https://apps.usw2.pure.cloud/genesys-bootstrap/genesys.min.js',
    {
      environment: 'usw2',
      deploymentId: deploymentId,
    },
  );
};

/**
 * Genesys chat widget for GFiber site
 */
export const GenesysChat: React.FC<GenesysChatProps> = ({
  chatSource,
  deploymentId,
  environment,
  pageName,
  webPageAddress,
  openOnLoad,
  onClosed,
  showLauncher = false,
}) => {
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Keep track of latest props to send to Genesys without triggering re-renders/re-subscriptions
  const propsRef = useRef({
    environment,
    chatSource,
    pageName,
    webPageAddress,
  });
  propsRef.current = {environment, chatSource, pageName, webPageAddress};

  const setGenesysAttributes = useCallback(() => {
    if (typeof window === 'undefined' || !window.Genesys) return;

    const browserLocale = navigator.language?.startsWith('es')
      ? 'es-us'
      : 'en-us';

    const {environment, chatSource, pageName, webPageAddress} =
      propsRef.current;

    window.Genesys('command', 'Database.set', {
      messaging: {
        customAttributes: {
          fiber_widget_enabled: 'true',
          environment: environment,
          chatSource: chatSource,
          page_name: sanitize(pageName ?? document.title),
          webPageAddress: sanitize(webPageAddress ?? window.location.href),
          locale: browserLocale,
        },
      },
    });
  }, []);

  // Sync route/prop changes to Genesys immediately
  useEffect(() => {
    // Explicitly reference props so Biome recognizes them as dependencies
    if (environment || chatSource || pageName || webPageAddress) {
      setGenesysAttributes();
    }
  }, [environment, chatSource, pageName, webPageAddress, setGenesysAttributes]);

  useEffect(() => {
    if (!deploymentId || deploymentId === 'null') return;
    // Genesys Messenger Initialization
    ((
      g: Window,
      e: 'Genesys',
      n: string,
      es: {environment: string; deploymentId: string},
    ) => {
      if (g[e]) return;
      g._genesysJs = e;
      const fn: GenesysFunction = (...args: unknown[]) => {
        (fn.q = fn.q || []).push(args);
      };
      g[e] = fn;

      fn.t = Date.now();
      fn.c = es;

      const ys = document.createElement('script');
      ys.async = true;
      ys.src = n;
      ys.charset = 'utf-8';
      document.head.appendChild(ys);
    })(
      window,
      'Genesys',
      'https://apps.usw2.pure.cloud/genesys-bootstrap/genesys.min.js',
      {
        environment: 'usw2',
        deploymentId: deploymentId,
      },
    );

    const styleId = 'genesys-launcher-hider';
    const removeStyle = () => {
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };

    function handleReadyHide() {
      window.Genesys?.('command', 'Launcher.hide');

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      // Delay removing the style to allow the Launcher.hide command to take effect.
      timerRef.current = setTimeout(() => {
        removeStyle();
      }, HIDE_LAUNCHER_STYLE_REMOVAL_DELAY_MS);
    }

    function handleOpenedHide() {
      removeStyle();
      setGenesysAttributes();
    }

    function handleOpenedShow() {
      setGenesysAttributes();
    }

    function handleCleared() {
      setGenesysAttributes();
    }

    function handleClosed() {
      onClosedRef.current?.();
    }

    if (window.Genesys) {
      if (!showLauncher) {
        if (!document.getElementById(styleId)) {
          const style = document.createElement('style');
          style.id = styleId;
          style.textContent = `
            #genesys-mpp-wrapper,
            #genesys-messenger {
              visibility: hidden !important;
              opacity: 0 !important;
            }
          `;
          document.head.appendChild(style);
        }

        window.Genesys('command', 'Launcher.hide');
        window.Genesys('subscribe', 'Messenger.ready', handleReadyHide);
        window.Genesys('subscribe', 'Messenger.opened', handleOpenedHide);
      } else {
        removeStyle();
        window.Genesys('command', 'Launcher.show');
        window.Genesys('subscribe', 'Messenger.opened', handleOpenedShow);
      }

      // CRITICAL FIX: If the user clicks the trash can, the session clears.
      // Re-apply attributes so if they start a new chat, it has the correct customAttributes.
      window.Genesys('subscribe', 'MessagingService.cleared', handleCleared);

      if (openOnLoad) {
        window.Genesys('command', 'Messenger.open');
      }

      window.Genesys('subscribe', 'Messenger.closed', handleClosed);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      const style = document.getElementById('genesys-launcher-hider');
      if (style) {
        style.remove();
      }

      if (window.Genesys) {
        window.Genesys('unsubscribe', 'Messenger.ready', handleReadyHide);
        window.Genesys('unsubscribe', 'Messenger.opened', handleOpenedHide);
        window.Genesys('unsubscribe', 'Messenger.opened', handleOpenedShow);
        window.Genesys(
          'unsubscribe',
          'MessagingService.cleared',
          handleCleared,
        );
        window.Genesys('unsubscribe', 'Messenger.closed', handleClosed);
      }
    };
  }, [deploymentId, openOnLoad, showLauncher, setGenesysAttributes]);

  return null;
};
